import React, { memo, useCallback, useRef, useMemo } from 'react';
import { View, Platform, Pressable } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import * as Animatable from 'react-native-animatable';
import { Button, XStack, SizableText } from 'tamagui';
import { AntDesign } from '@expo/vector-icons';
import LottieView from '@utils/lottieAnimation';
import { HIDE_PAYMENT_REQUIRED } from '@reduxLocal/actions/types';
import { DefaultRootState } from '@reduxLocal/persistState';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { isTablet } from 'react-native-device-info';
import { useNavigation } from '@react-navigation/native';
import { normalizeStyles } from '@utils/styleNormalizer';

interface PaymentRequiredBannerProps {
  subscriptionData: any;
  onUpgrade: () => void;
}

/**
 * Isolated PaymentRequiredBanner component
 * Only re-renders when paymentRequired state changes
 * Prevents parent DashboardNavigator from re-rendering
 */
const PaymentRequiredBanner: React.FC<PaymentRequiredBannerProps> = ({ subscriptionData, onUpgrade }) => {
  const dispatch = useDispatch();
  const theme = themeSelector();
  const navigation = useNavigation();

  // Selector is isolated to thiscomponent - parent won't re-render when this changes
  const paymentRequired = useSelector((state: DefaultRootState) => state.UI?.paymentRequiredVisible);
  const paymentMessage = useSelector((state: DefaultRootState) => state.UI?.paymentMessage);

  const handleHidePayment = useCallback(() => dispatch({ type: HIDE_PAYMENT_REQUIRED }), [dispatch]);

  // Normalize button styles to prevent border style conflicts
  // Use separate border properties instead of shorthand to avoid conflicts with Tamagui's internal styles
  const webButtonStyle = useMemo(() => normalizeStyles({
    maxHeight: 35,
    maxWidth: 140,
    top: -5,
    backgroundColor: "$primary",
    alignSelf: 'flex-end',
  }), []);

  const nativeButtonStyle = useMemo(() => normalizeStyles({
    backgroundColor: "rgba(255,255,255,0.2)",
    flex: 1,
    alignSelf: 'flex-end',
    top: -8,
  }), []);

  // Don't render anything if conditions aren't met
  if (!subscriptionData || !paymentRequired) {
    return null;
  }

  return (
    <Animatable.View
      useNativeDriver
      animation={'slideInDown'}
      style={{
        paddingHorizontal: "5%",
        marginHorizontal: "2%",
        top: Platform.select({ native: isTablet ? 90 : 70, web: 70 }),
        borderColor: 'red',
        zIndex: 100,
        shadowRadius: 20,
        shadowOpacity: 0.2,
        shadowOffset: { width: 0, height: 2 },
        minHeight: 40,
        borderRadius: 20,
        position: "absolute",
        backgroundColor: Platform.select({ native: theme ? "pink" : "rgba(255, 192, 203, 0.8)", web: theme ? "pink" : "rgba(255, 192, 203, 0.8)" }),
        alignSelf: "stretch",
        minWidth: "96%"
      }}
    >
      {Platform.OS === 'web' ? (
        <XStack alignSelf='stretch' alignItems='center' justifyContent='space-between' paddingHorizontal="$4" gap="$2">
          <SizableText
            fontSize={14}
            userSelect="none"
            fontWeight="600"
            color="#dc2626"
            numberOfLines={2}
            flex={1}
          >
            {paymentMessage || 'You have exceeded the limits of your current plan, you may not be able to add any more data until you upgrade.'}
          </SizableText>
          <Button borderRadius={0} style={webButtonStyle} onPress={onUpgrade}>
            Upgrade
          </Button>
          <Pressable onPress={handleHidePayment} style={{ padding: 8 }}>
            <AntDesign name="close" size={24} color="black" />
          </Pressable>
        </XStack>
      ) : (
        <XStack alignSelf='stretch' alignContent='space-between' overflow='hidden' paddingTop={3} flex={1}>
          <Pressable style={{ width: 40 }} onPress={handleHidePayment}>
            <AntDesign name="close" size={24} color="black" style={{ position: 'absolute', right: 10, top: 3 }} />
          </Pressable>
          <LottieView source={require('@assets/animations/exclamation-mark.json')} autoPlay style={{ top: 2, flex: 1, maxHeight: 30, maxWidth: 40 }} />
            <SizableText
              fontSize={12}
              fontWeight="600"
              color={theme ? "#1e293b" : "#f1f5f9"}
              numberOfLines={2}
              flex={1}
              style={{ top: 2 }}
            >
              {paymentMessage || 'You have exceeded the limits of your current plan, you may not be able to add any more data until you upgrade.'}
            </SizableText>
            <Button borderRadius={0} style={nativeButtonStyle} onPress={onUpgrade}>
            Upgrade
          </Button>
        </XStack>
      )}
    </Animatable.View>
  );
};

export default memo(PaymentRequiredBanner);

